/**
 * PinSheet — единый PIN-UX для worker-flow (login §3.3 и signature §3.6).
 *
 * Mobile-first: bottom-sheet с drag-handle. На десктопе тот же `Sheet` снизу —
 * не центрированный диалог, чтобы PIN-paddle руки оставался у нижнего края
 * экрана и не приходилось тянуться к центру (factory-floor планшет на стене).
 *
 * Состояния (см. §3.2 assessment):
 *   idle      — обычный ввод
 *   submitting — pad disabled, dots dim, drag-close БЛОКИРОВАН (assessment'ом
 *                просили — иначе свайп в момент submit оставит «висящий» fetch)
 *   error     — alert-red dots, shake, attempt counter «1/3», pad очищен и
 *                снова кликабелен (idle), счётчик показывается до следующего
 *                удачного submit
 *   lockout   — контент заменяется на single-call-to-action «Закрыть»
 *                (423 от backend, см. §1.4)
 *
 * Семантика closes:
 *   - Свайп вниз / ✕ / Esc — `onClose(false)` (без коммита) ИЛИ блок при submitting.
 *   - Успешный submit — родитель решает: вызвать `setOpen(false)` сам.
 */
import * as React from "react";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NumericPad } from "@/components/field/NumericPad";
import { cn } from "@/lib/utils";
import {
  FormFieldError,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";

export type PinSheetState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | {
      kind: "error";
      attempts: number;
      maxAttempts: number;
      message?: string;
      requestId?: string | null;
    }
  | { kind: "lockout"; message?: string; requestId?: string | null }
  | { kind: "success" };

export interface PinSheetProps {
  open: boolean;
  /** Закрытие свайпом / ✕ / Esc. НЕ зовётся при submitting. */
  onOpenChange: (open: boolean) => void;
  /** Заголовок (обычно «ПИН-КОД»). */
  title: string;
  /** Подзаголовок-контекст: «Вход в систему» / «Подпись отчёта». */
  description?: string;
  /** Внешняя state machine. Родитель управляет переходами. */
  state: PinSheetState;
  /** Async-обработчик подтверждённого PIN. */
  onSubmit: (pin: string) => void | Promise<void>;
  /** Лейбл главной CTA в lockout-режиме (default — «Закрыть»). */
  lockoutCtaLabel?: string;
  /** data-testid на корень. */
  "data-testid"?: string;
}

const PIN_LENGTH = 4;

export function PinSheet({
  open,
  onOpenChange,
  title,
  description,
  state,
  onSubmit,
  lockoutCtaLabel,
  "data-testid": testId = "pin-sheet",
}: PinSheetProps): React.ReactElement {
  const { t } = useTranslation();

  // PIN-state очищается двумя способами:
  //   1. Sheet закрылся (open=false) — следующее открытие должно быть с пустым PIN.
  //   2. Родитель переключил state в "error" (неверный PIN) — на новую попытку
  //      pad должен быть чист (assessment §3.3 «на 401 PIN-pad очищается»).
  // Оба триггера — внешние props. Вместо useEffect+setState (React Compiler
  // ругается на set-state-in-effect, см. `frontend/CLAUDE.md`) — выносим
  // PIN-state в `PinPadBody` и пересоздаём его через `key`-reset.
  const resetKey = `${open ? "o" : "c"}:${state.kind}:${state.kind === "error" ? state.attempts : 0}`;

  const isSubmitting = state.kind === "submitting";
  const isLockout = state.kind === "lockout";

  // Блок закрытия по свайпу/Esc при submitting (assessment §3.2) и при
  // success (родитель сам закроет — иначе двойной close может прервать
  // success-анимацию).
  const handleOpenChange = (next: boolean): void => {
    if (!next && (isSubmitting || state.kind === "success")) return;
    onOpenChange(next);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={!isSubmitting && !isLockout}
        data-testid={testId}
        // bottom-sheet: ограничиваем по ширине на десктопе, на mobile — full bleed
        className="mx-auto w-full max-w-[420px] gap-0 rounded-t-[6px] border-x p-0"
        style={{
          background: "var(--bg-panel-0)",
          borderColor: "var(--rule-line)",
          paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
        }}
        // При submitting блокируем outside-click и Esc вручную — Radix не
        // знает про наш state, поэтому intercept'им event'ы тут.
        onPointerDownOutside={(e) => {
          if (isSubmitting || state.kind === "success") e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting || state.kind === "success") e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isSubmitting || state.kind === "success") e.preventDefault();
        }}
      >
        {/* Drag-handle — визуальный affordance свайпа. Скрывается когда
            свайп заблокирован (submitting/success). Не функциональный — Radix
            обрабатывает свайп через onPointerDownOutside; handle — чисто
            про expectations. */}
        {!isSubmitting && state.kind !== "success" && (
          <div
            aria-hidden
            className="mx-auto mt-2 mb-1 h-1 w-9 rounded-full"
            style={{ background: "var(--rule-strong)" }}
          />
        )}

        <div className="flex flex-col items-center gap-3 px-5 pt-2 pb-5">
          <SheetTitle
            className="font-condensed text-base font-semibold tracking-[0.14em] uppercase"
            style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
          >
            {title}
          </SheetTitle>
          {description && (
            <SheetDescription
              className="text-center text-xs tracking-[0.04em] uppercase"
              style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            >
              {description}
            </SheetDescription>
          )}

          {isLockout ? (
            <LockoutBody
              message={
                (state.kind === "lockout" ? state.message : undefined) ??
                t("field.pin.lockout.message")
              }
              ctaLabel={lockoutCtaLabel ?? t("field.pin.lockout.close")}
              onClose={() => onOpenChange(false)}
              testId={testId}
            />
          ) : (
            <PinPadBody
              key={resetKey}
              open={open}
              state={state}
              onSubmit={onSubmit}
              title={title}
              testId={testId}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface PinPadBodyProps {
  open: boolean;
  state: PinSheetState;
  onSubmit: (pin: string) => void | Promise<void>;
  title: string;
  testId: string;
}

/**
 * Внутренний body PIN-pad'а. Полностью пересоздаётся через `key` каждый раз,
 * когда родителю нужно сбросить PIN (закрытие sheet / переход в error).
 * Это держит React Compiler счастливым (никаких setState в useEffect)
 * и упрощает локальный state до одного `useState`.
 */
function PinPadBody({ open, state, onSubmit, title, testId }: PinPadBodyProps): React.ReactElement {
  const { t } = useTranslation();
  const [pin, setPin] = React.useState("");
  useReleaseDirtyState(open && pin !== "");

  const isSubmitting = state.kind === "submitting";
  const isError = state.kind === "error";
  const errorMessage = isError
    ? (state.message ??
      t("field.pin.error.attempt", {
        attempt: state.attempts,
        max: state.maxAttempts,
      }))
    : null;
  const errorIssue: FormValidationIssue | undefined = errorMessage
    ? {
        id: `${testId}-error`,
        message: errorMessage,
        kind: state.kind === "error" && state.message ? "server" : "field",
        controlId: `${testId}-pad`,
        requestId: state.kind === "error" ? state.requestId : null,
      }
    : undefined;

  React.useEffect(() => {
    if (errorMessage) requestAnimationFrame(() => focusValidationControl(`${testId}-pad`));
  }, [errorMessage, testId]);

  const handleSubmit = (value: string): void => {
    if (isSubmitting) return;
    void onSubmit(value);
  };

  return (
    <>
      <PinDots
        length={PIN_LENGTH}
        filled={pin.length}
        error={isError}
        submitting={isSubmitting}
        ariaLabel={t("field.pin.dotsAria", { filled: pin.length, length: PIN_LENGTH })}
        testId={`${testId}-dots`}
      />

      <FormFieldError issue={errorIssue} />

      {isSubmitting && (
        <p
          data-testid={`${testId}-submitting`}
          className="font-condensed text-xs tracking-[0.08em] uppercase"
          style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
        >
          {t("field.pin.submitting")}
        </p>
      )}

      <NumericPad
        id={`${testId}-pad`}
        length={PIN_LENGTH}
        value={pin}
        onChange={setPin}
        onSubmit={handleSubmit}
        disabled={isSubmitting}
        aria-label={title}
        aria-invalid={Boolean(errorIssue)}
        aria-describedby={errorIssue?.id}
        data-testid={`${testId}-pad`}
      />
    </>
  );
}

interface PinDotsProps {
  length: number;
  filled: number;
  error: boolean;
  submitting: boolean;
  ariaLabel: string;
  testId: string;
}

function PinDots({
  length,
  filled,
  error,
  submitting,
  ariaLabel,
  testId,
}: PinDotsProps): React.ReactElement {
  return (
    <div
      data-testid={testId}
      role="status"
      aria-label={ariaLabel}
      className={cn("flex items-center gap-2", error && "pin-dots-shake")}
      style={{
        animation: error ? "pin-shake 360ms var(--ease-mech)" : undefined,
      }}
    >
      {Array.from({ length }, (_, i) => {
        const isFilled = i < filled;
        return (
          <span
            key={i}
            data-testid={`${testId}-cell-${i}`}
            data-filled={isFilled || undefined}
            className="inline-block transition-colors duration-100"
            style={{
              width: 18,
              height: 18,
              borderRadius: "var(--radius-1)",
              border: "1px solid var(--rule-strong)",
              background:
                error && isFilled
                  ? "var(--alert-red)"
                  : isFilled
                    ? "var(--accent-agent)"
                    : "transparent",
              opacity: submitting ? 0.5 : 1,
            }}
          />
        );
      })}
      <style>{`
        @keyframes pin-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}

interface LockoutBodyProps {
  message: string | undefined;
  ctaLabel: string;
  onClose: () => void;
  testId: string;
}

function LockoutBody({ message, ctaLabel, onClose, testId }: LockoutBodyProps): React.ReactElement {
  return (
    <div
      data-testid={`${testId}-lockout`}
      className="flex flex-col items-center gap-4 px-2 py-4 text-center"
    >
      <div
        aria-hidden
        className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-2)]"
        style={{
          background: "var(--alert-red)",
          color: "#fff",
          boxShadow: "var(--shadow-plate)",
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M6 10V7a6 6 0 1 1 12 0v3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="square"
          />
          <rect x="4" y="10" width="16" height="11" rx="1" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      <p
        className="font-condensed text-sm leading-snug"
        style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
      >
        {message ?? "Учётная запись заблокирована. Обратитесь к руководителю."}
      </p>
      <Button
        type="button"
        variant="outline"
        size="default"
        onClick={onClose}
        data-testid={`${testId}-lockout-close`}
        className="min-w-[140px]"
      >
        {ctaLabel}
      </Button>
    </div>
  );
}
